import React, { useEffect, useMemo, useRef, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StyleConstants from '../../../core/styles.constants';

// Zones come from Intl.supportedValuesOf (Chrome 99+, FF 93+, Safari 15.4+),
// falling back to ['UTC']. They are grouped by region prefix (continent) so
// admins can browse, and filtered by a search input.
interface TimezoneOption {
  label: string;
  value: string;
}

interface TimezoneGroup {
  region: string;
  zones: TimezoneOption[];
}

/** Returns the current UTC offset of a timezone in whole minutes (e.g. +330 for IST). */
function getTzOffsetMinutes(tz: string, now: Date): number {
  try {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    }).formatToParts(now);
    const raw = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT+0';
    const match = raw.match(/GMT([+-])(\d+)(?::(\d+))?/);
    if (!match) return 0;
    const sign = match[1] === '+' ? 1 : -1;
    return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3] ?? '0', 10));
  } catch {
    return 0;
  }
}

/** Formats an offset in minutes as e.g. "UTC+05:30" or "UTC-04:00". */
function fmtOffset(minutes: number): string {
  const sign = minutes >= 0 ? '+' : '-';
  const abs = Math.abs(minutes);
  const h = String(Math.floor(abs / 60)).padStart(2, '0');
  const m = String(abs % 60).padStart(2, '0');
  return `UTC${sign}${h}:${m}`;
}

// Built once at module load. Groups zones by continent/region prefix.
const TIMEZONE_GROUPS: TimezoneGroup[] = ((): TimezoneGroup[] => {
  type IntlExt = typeof Intl & { supportedValuesOf?: (key: string) => string[] };
  const { supportedValuesOf } = Intl as IntlExt;
  const allZones: string[] =
    typeof supportedValuesOf === 'function'
      ? supportedValuesOf('timeZone')
      : ['UTC'];

  const now = new Date();
  const regionMap = allZones.reduce((map, tz) => {
    const slashIdx = tz.indexOf('/');
    // 'Etc/*' and bare identifiers like 'UTC' go into General
    const region =
      slashIdx === -1 || tz.startsWith('Etc/') ? 'General' : tz.slice(0, slashIdx);
    const offsetMinutes = getTzOffsetMinutes(tz, now);
    if (!map.has(region)) map.set(region, []);
    const bucket = map.get(region);
    if (bucket != null) {
      bucket.push({ label: `(${fmtOffset(offsetMinutes)}) ${tz}`, value: tz, offsetMinutes });
    }
    return map;
  }, new Map<string, Array<TimezoneOption & { offsetMinutes: number }>>());

  // General first, then alphabetical by region name
  const sortedRegions = Array.from(regionMap.keys()).sort((a, b) => {
    if (a === 'General') return -1;
    if (b === 'General') return 1;
    return a.localeCompare(b);
  });

  return sortedRegions.map((region) => ({
    region,
    zones: (regionMap.get(region) ?? [])
      .sort((a, b) => {
        if (a.offsetMinutes !== b.offsetMinutes) return a.offsetMinutes - b.offsetMinutes;
        return a.value.localeCompare(b.value);
      })
      .map(({ label, value }) => ({ label, value })),
  }));
})();

// ── TimezoneSelector
interface TimezoneSelectorProps {
  readonly value: string;
  readonly onChange: (tz: string) => void;
  readonly disabled: boolean;
  readonly searchPlaceholder: string;
}

const TimezoneSelector = ({
  value,
  onChange,
  disabled,
  searchPlaceholder,
}: TimezoneSelectorProps): React.JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleMouseDown = (e: MouseEvent): void => {
      if (containerRef.current != null && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return (): void => { document.removeEventListener('mousedown', handleMouseDown); };
  }, [isOpen]);

  // Resolve display label from groups
  const selectedLabel = useMemo((): string => {
    const found = TIMEZONE_GROUPS
      .flatMap((g) => g.zones)
      .find((z) => z.value === value);
    return found?.label ?? value;
  }, [value]);

  // Filter groups by search query
  const filteredGroups = useMemo((): TimezoneGroup[] => {
    const q = search.trim().toLowerCase();
    if (q === '') return TIMEZONE_GROUPS;
    return TIMEZONE_GROUPS
      .map((g) => ({ ...g, zones: g.zones.filter((z) => z.label.toLowerCase().includes(q) || z.value.toLowerCase().includes(q)) }))
      .filter((g) => g.zones.length > 0);
  }, [search]);

  const handleSelect = (tz: string): void => {
    onChange(tz);
    setIsOpen(false);
    setSearch('');
  };

  const handleToggle = (): void => {
    if (!disabled) setIsOpen((prev) => !prev);
  };

  const triggerStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6,
    padding: '7px 8px',
    boxSizing: 'border-box',
    borderRadius: 6,
    border: `1px solid ${isOpen ? StyleConstants.MEDIUM_BLUE_COLOR : 'rgba(36,55,75,0.25)'}`,
    fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
    fontSize: StyleConstants.TERTIARY_FONT_SIZE,
    backgroundColor: disabled ? 'rgba(0,0,0,0.03)' : '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer',
    color: StyleConstants.GREY_COLOR,
    textAlign: 'left',
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={handleToggle}
        style={triggerStyle}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span style={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textAlign: 'left',
        }}
        >
          {selectedLabel}
        </span>
        <ExpandMoreIcon
          style={{
            fontSize: 16,
            color: StyleConstants.GREY_COLOR,
            opacity: 0.7,
            flexShrink: 0,
            transition: 'transform 0.15s ease',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            zIndex: 1000,
            top: 'calc(100% + 2px)',
            left: 0,
            right: 0,
            backgroundColor: '#fff',
            border: '1px solid rgba(36,55,75,0.2)',
            borderRadius: 8,
            boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Search input */}
          <div style={{ padding: '8px 8px 4px', borderBottom: '1px solid rgba(36,55,75,0.1)' }}>
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              type="text"
              value={search}
              onChange={(e): void => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              style={{
                width: '100%',
                padding: '6px 8px',
                boxSizing: 'border-box',
                border: '1px solid rgba(36,55,75,0.2)',
                borderRadius: 5,
                fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
                fontSize: StyleConstants.TERTIARY_FONT_SIZE,
                outline: 'none',
              }}
            />
          </div>

          {/* Grouped list */}
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {filteredGroups.length === 0 && (
              <div style={{
                padding: '10px 12px',
                fontSize: StyleConstants.TERTIARY_FONT_SIZE,
                color: StyleConstants.GREY_COLOR,
                opacity: 0.6,
                fontStyle: 'italic',
              }}
              >
                No results
              </div>
            )}
            {filteredGroups.map((group, groupIndex) => (
              <div key={group.region}>
                {/* Region header — sticky within its own group container so it scrolls
                    away naturally once the group leaves the viewport. A solid background
                    and z-index prevent zone rows from showing through as they scroll under. */}
                <div style={{
                  padding: '5px 10px 3px',
                  marginTop: groupIndex === 0 ? 0 : 4,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: StyleConstants.GREY_COLOR,
                  backgroundColor: '#f4f5f6',
                  borderTop: groupIndex === 0 ? 'none' : '1px solid rgba(36,55,75,0.08)',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                }}
                >
                  {group.region}
                </div>
                {/* Zone rows */}
                {group.zones.map((tz) => (
                  <button
                    key={tz.value}
                    type="button"
                    role="option"
                    aria-selected={tz.value === value}
                    onClick={(): void => handleSelect(tz.value)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '6px 12px',
                      border: 'none',
                      background: tz.value === value ? 'rgba(46,120,213,0.08)' : 'transparent',
                      fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
                      fontSize: StyleConstants.TERTIARY_FONT_SIZE,
                      color: tz.value === value ? StyleConstants.MEDIUM_BLUE_COLOR : StyleConstants.GREY_COLOR,
                      fontWeight: tz.value === value ? 600 : 400,
                      cursor: 'pointer',
                    }}
                  >
                    {tz.label}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export { getTzOffsetMinutes };
export default TimezoneSelector;
